import { Injectable } from '@angular/core';
import { Policy } from '@enrollhere/classes';

/**
 * Policy Cache Service
 * Client-side caching for policies
 * Reusable across all products
 */
@Injectable({
  providedIn: 'root',
})
export class PolicyCacheService {
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private readonly cache = new Map<
    string,
    { policy: Policy; timestamp: number }
  >();

  /**
   * Get policy from cache if valid
   */
  get(policyId: string): Policy | null {
    const cached = this.cache.get(policyId);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > this.CACHE_TTL) {
      // Cache expired
      this.cache.delete(policyId);
      return null;
    }

    return cached.policy;
  }

  /**
   * Set policy in cache
   */
  set(policyId: string, policy: Policy): void {
    this.cache.set(policyId, {
      policy,
      timestamp: Date.now(),
    });
  }

  /**
   * Invalidate cache for a policy
   */
  invalidate(policyId: string): void {
    this.cache.delete(policyId);
  }

  /**
   * Clear entire cache
   */
  clearAll(): void {
    this.cache.clear();
  }

  /**
   * Clean up expired cache entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [policyId, cached] of this.cache.entries()) {
      if (now - cached.timestamp > this.CACHE_TTL) {
        this.cache.delete(policyId);
      }
    }
  }
}
